import { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Box, Chip, Link, Paper, Skeleton, Stack, Typography } from '@mui/material';
import redirectOnAnalytics from '~/utils/redirectOnAnalytics.js';
import { getSourcesChartOption, metricConfig, metrics } from './utils/SourcesConfig.js';
import './styles/source.css';
import AppConfig from '~/config/AppConfig.js';
import getCurrencySymbol from '~/utils/getCurrencySymbol.js';

function Sources({ sources = [], isLoading = false, startDate, endDate }) {
  const [selectedMetric, setSelectedMetric] = useState('Revenue');
  const chartRef = useRef(null);
  const hasSourceData = sources.length > 0;

  const chartOption = useMemo(
    () => getSourcesChartOption({ sources, selectedMetric }),
    [selectedMetric, sources]
  );

  useEffect(() => {
    if (isLoading || !hasSourceData) {
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      chartRef.current?.getEchartsInstance?.()?.resize();
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [chartOption, hasSourceData, isLoading]);

  return (
    <Paper
      elevation={2}
      className="sources-card">
      <Typography
        variant="subtitle1"
        className="sources-title">
        Top 5 Sources
      </Typography>

      {isLoading ? (
        <Box className="sources-chart-wrap">
          <Skeleton
            variant="rounded"
            className="sources-chart"
            width={'100%'}
            height={'100%'}
          />
        </Box>
      ) : null}

      {!isLoading && hasSourceData ? (
        <>
          <Stack
            direction="row"
            spacing={1}
            className="sources-chip-row">
            {metrics.map(metric => {
              const isActive = metric === selectedMetric;
              const cfg = metricConfig[metric];

              return (
                <Chip
                  key={metric}
                  clickable
                  onClick={() => setSelectedMetric(metric)}
                  label={
                    <Box className="sources-chip-label">
                      <Box
                        className="sources-chip-dot"
                        sx={{ bgcolor: isActive ? '#ffffff' : cfg.chipColor }}
                      />
                      <span>
                        {metric === 'Revenue' ? `Revenue (${getCurrencySymbol()})` : cfg.chipLabel}
                      </span>
                    </Box>
                  }
                  variant={isActive ? 'filled' : 'outlined'}
                  className="sources-chip"
                  sx={{
                    bgcolor: isActive ? cfg.barColor : '#ffffff',
                    color: isActive ? '#ffffff' : '#202020',
                    borderColor: isActive ? cfg.barColor : '#cfd6df',
                    '&:hover': {
                      bgcolor: isActive ? cfg.barColor : '#f8f9fb',
                      opacity: isActive ? 0.92 : 1,
                    },
                  }}
                />
              );
            })}
          </Stack>

          <Box className="sources-chart-wrap">
            <ReactECharts
              ref={chartRef}
              option={chartOption}
              className="sources-chart"
              style={{ width: '100%', height: '100%' }}
              opts={{ renderer: 'canvas' }}
              autoResize
              notMerge
            />
          </Box>
        </>
      ) : null}

      {!isLoading && !hasSourceData ? (
        <Box className="sources-empty">
          <Box>
            <Typography
              variant="body1"
              className="sources-empty-title">
              No source data is available yet
            </Typography>
            <Typography
              variant="body1"
              className="sources-empty-subtitle">
              Data will appear here once available
            </Typography>
          </Box>
        </Box>
      ) : null}

      <Box className="sources-link-wrap">
        <Link
          component="button"
          className="sources-link sas-report-link"
          onClick={() =>
            redirectOnAnalytics({ groups: AppConfig.inventoryReportGroups, startDate, endDate })
          }>
          Open Inventory Report -&gt;
        </Link>
      </Box>
    </Paper>
  );
}

export default Sources;
